/**
 * Featured Releases — tab switching + native scroll-snap product sliders.
 * Mirrors the live section: tabs swap pre-rendered panels, each panel is a
 * horizontal scroll-snap track with hover arrows and mouse drag on desktop.
 * @extends HTMLElement
 */
class FeaturedReleases extends HTMLElement {
  connectedCallback() {
    this.abortController = new AbortController();
    const { signal } = this.abortController;

    this.tabs = Array.from(this.querySelectorAll('[data-tab]'));
    this.panels = Array.from(this.querySelectorAll('[data-panel]'));

    for (const tab of this.tabs) {
      tab.addEventListener('click', () => this.selectTab(Number(tab.dataset.tab)), { signal });
    }

    for (const slider of this.querySelectorAll('[data-slider]')) {
      this.initSlider(slider, signal);
    }

    window.addEventListener('resize', () => this.updateAllArrows(), { signal });
    this.updateAllArrows();
  }

  disconnectedCallback() {
    this.abortController?.abort();
  }

  selectTab(index) {
    for (const [i, tab] of this.tabs.entries()) {
      tab.classList.toggle('is-active', i === index);
      tab.setAttribute('aria-selected', i === index ? 'true' : 'false');
    }
    for (const [i, panel] of this.panels.entries()) {
      panel.classList.toggle('is-active', i === index);
      panel.toggleAttribute('hidden', i !== index);
    }
    // The newly shown track had zero dimensions while hidden
    this.updateAllArrows();
  }

  initSlider(slider, signal) {
    const track = slider.querySelector('[data-track]');
    const prev = slider.querySelector('[data-arrow-prev]');
    const next = slider.querySelector('[data-arrow-next]');
    if (!track) return;

    const scrollByPage = (direction) => {
      track.scrollBy({ left: direction * track.clientWidth * 0.8, behavior: 'smooth' });
    };

    prev?.addEventListener('click', () => scrollByPage(-1), { signal });
    next?.addEventListener('click', () => scrollByPage(1), { signal });

    // Throttle to one arrow update per frame — scroll fires far more often
    let scrollRaf = null;
    track.addEventListener(
      'scroll',
      () => {
        if (scrollRaf) return;
        scrollRaf = window.requestAnimationFrame(() => {
          scrollRaf = null;
          this.updateArrows(slider);
        });
      },
      { signal, passive: true }
    );

    this.bindDrag(track, signal);
  }

  /* Mouse drag-to-scroll (touch already scrolls natively). Clicks are
     suppressed only when an actual drag happened. */
  bindDrag(track, signal) {
    let startX = 0;
    let startScroll = 0;
    let dragging = false;
    let moved = false;

    track.addEventListener(
      'pointerdown',
      (event) => {
        if (event.pointerType !== 'mouse') return;
        if (event.target.closest('.card-media-swiper')) return;
        dragging = true;
        moved = false;
        startX = event.clientX;
        startScroll = track.scrollLeft;
      },
      { signal }
    );

    track.addEventListener(
      'pointermove',
      (event) => {
        if (!dragging) return;
        const delta = event.clientX - startX;
        if (!moved && Math.abs(delta) < 5) return;
        if (!moved) {
          moved = true;
          track.classList.add('is-dragging');
          track.setPointerCapture(event.pointerId);
        }
        track.scrollLeft = startScroll - delta;
      },
      { signal }
    );

    const endDrag = (event) => {
      if (!dragging) return;
      dragging = false;
      if (moved) {
        track.classList.remove('is-dragging');
        if (track.hasPointerCapture?.(event.pointerId)) {
          track.releasePointerCapture(event.pointerId);
        }
        // The click event (if any) fires synchronously after pointerup —
        // clear the flag right after so future clicks aren't swallowed
        window.setTimeout(() => {
          moved = false;
        }, 0);
      }
    };

    track.addEventListener('pointerup', endDrag, { signal });
    track.addEventListener('pointercancel', endDrag, { signal });

    // Swallow the click that follows a drag so cards don't navigate
    track.addEventListener(
      'click',
      (event) => {
        if (moved) {
          event.preventDefault();
          event.stopPropagation();
        }
      },
      { signal, capture: true }
    );
  }

  updateArrows(slider) {
    const track = slider.querySelector('[data-track]');
    const prev = slider.querySelector('[data-arrow-prev]');
    const next = slider.querySelector('[data-arrow-next]');
    if (!track) return;

    const maxScroll = track.scrollWidth - track.clientWidth;
    if (prev) prev.disabled = track.scrollLeft <= 1;
    if (next) next.disabled = track.scrollLeft >= maxScroll - 1;
  }

  /* Live site centers slider arrows on the product image (not the whole
     card) via a JS-computed offset — replicated with the --fr-arrow-top
     variable below. Batched into a read phase then a write phase — interleaving
     getBoundingClientRect with style writes forces a reflow per slider */
  updateAllArrows() {
    const measurements = [];

    for (const slider of this.querySelectorAll('[data-slider]')) {
      const track = slider.querySelector('[data-track]');
      if (!track) continue;

      let center = null;
      const media = slider.querySelector('.card-media');
      if (media) {
        const mediaRect = media.getBoundingClientRect();
        if (mediaRect.height > 0) {
          const sliderRect = slider.getBoundingClientRect();
          center = Math.round(mediaRect.top - sliderRect.top + mediaRect.height / 2);
        }
      }

      measurements.push({
        slider,
        prev: slider.querySelector('[data-arrow-prev]'),
        next: slider.querySelector('[data-arrow-next]'),
        scrollLeft: track.scrollLeft,
        maxScroll: track.scrollWidth - track.clientWidth,
        center,
      });
    }

    for (const m of measurements) {
      if (m.prev) m.prev.disabled = m.scrollLeft <= 1;
      if (m.next) m.next.disabled = m.scrollLeft >= m.maxScroll - 1;
      if (m.center !== null) m.slider.style.setProperty('--fr-arrow-top', `${m.center}px`);
    }
  }
}

if (!customElements.get('featured-releases')) {
  customElements.define('featured-releases', FeaturedReleases);
}
